import { Component, Input, OnInit, OnDestroy, ChangeDetectorRef, TemplateRef, ViewChild, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Subject } from 'rxjs';
import { takeUntil, filter } from 'rxjs/operators';

import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { DateAdapter, MatOptionModule } from '@angular/material/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatDialog, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { TableSurfaceComponent } from '@shared/components/table-surface/table-surface.component';
import { TableColumn } from '@shared/components/data-table/data-table.component';
import { ToolbarSortSelection } from '@shared/components/table-toolbar/table-toolbar.component';
import { NotificationService } from '@shared/services/notification.service';
import { modalDialogConfig } from '@shared/components/modal-shell/modal-dialog.config';
import { ModalShellComponent } from '@shared/components/modal-shell/modal-shell.component';
import { UserFullInfoService } from '@core/services/user-full-info.service';
import { BreadcrumbService } from '@core/services/breadcrumb.service';
import { UnitService } from '../../services/unit.service';
import { OrganizationUnit, UnitEvent, OrganizationLevel } from '../../models/unit.model';
import { OrganizationUnitEventTypeDto, OrganizationUnitEventTypeManagementService } from '@features/configuration/services/organization-unit-event-type-management.service';
import { DeleteConfirmDialogComponent, DeleteConfirmDialogData } from '@features/user/components/shared/delete-confirm-dialog/delete-confirm-dialog.component';
import { organizationUnitEventTypeIcons } from '@core/api/enums/organization-unit-event-type.labels';
import { DatePickerComponent } from '@shared/components/date-picker/date-picker.component';

@Component({
  selector: 'app-unit-events-page',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatOptionModule,
    MatButtonModule,
    MatIconModule,
    MatProgressSpinnerModule,
    MatTooltipModule,
    MatDialogModule,
    TableSurfaceComponent,
    DatePickerComponent,
    ModalShellComponent,
  ],
  templateUrl: './unit-events-page.component.html',
  styleUrl: './unit-events-page.component.scss',
})
export class UnitEventsPageComponent implements OnInit, OnDestroy {
  @Input() unit: OrganizationUnit | null = null;
  events: UnitEvent[] = [];
  isLoading = true;
  isSaving = false;
  editingEventId: number | null = null;
  errorMessage: string | null = null;

  private readonly userFullInfoService = inject(UserFullInfoService);
  private readonly dateAdapter = inject(DateAdapter);
  readonly canManageMemberData$ = this.userFullInfoService.canManageMemberData$;

  readonly columns: TableColumn[] = [
    { key: 'eventType', header: 'Tipo de evento', sortable: true, hideable: true, mobileTitle: true },
    { key: 'eventDate', header: 'Fecha', sortable: true, hideable: true },
    { key: 'notes', header: 'Notas', sortable: true, hideable: true },
    { key: 'actions', header: '', width: '96px', hideable: false, exportable: false },
  ];
  searchTerm = '';
  sortState: ToolbarSortSelection = { sortBy: 'eventDate', direction: 'asc' };

  get visibleEvents(): UnitEvent[] {
    const search = this.searchTerm.trim().toLocaleLowerCase('es');
    const value = (event: UnitEvent, key: string): string => {
      if (key === 'eventType') return this.getEventTypeLabel(event);
      if (key === 'notes') return event.notes ?? '';
      return event.eventDate;
    };

    return this.events
      .filter(event => !search || [this.getEventTypeLabel(event), event.eventDate, event.notes ?? '']
        .some(text => text.toLocaleLowerCase('es').includes(search)))
      .sort((left, right) => value(left, this.sortState.sortBy)
        .localeCompare(value(right, this.sortState.sortBy), 'es', { numeric: true })
        * (this.sortState.direction === 'asc' ? 1 : -1));
  }

  eventTypeOptions: OrganizationUnitEventTypeDto[] = [];

  eventForm: FormGroup;
  @ViewChild('eventDialog') eventDialog!: TemplateRef<unknown>;
  private eventDialogRef?: MatDialogRef<unknown>;

  private readonly destroy$ = new Subject<void>();

  constructor(
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly fb: FormBuilder,
    private readonly unitService: UnitService,
    private readonly eventTypeService: OrganizationUnitEventTypeManagementService,
    private readonly notifications: NotificationService,
    private readonly dialog: MatDialog,
    private readonly cdr: ChangeDetectorRef,
    private readonly breadcrumbService: BreadcrumbService,
  ) {
    this.eventForm = this.fb.group({
      eventTypeId: [null, Validators.required],
      eventDate: [null, Validators.required],
      notes: [''],
    });
  }

  getEventTypeLabel(event: UnitEvent): string {
    return event.eventTypeName || this.eventTypeOptions.find(t => t.value === event.eventTypeId)?.name || '';
  }

  getEventTypeIcon(event: UnitEvent): string {
    return organizationUnitEventTypeIcons[event.eventTypeId] || 'event';
  }

  ngOnInit(): void {
    if (this.unit) {
      this.loadEventTypes();
      this.loadEvents(this.unit.id);
      return;
    }

    const allParams = this.route.snapshot.pathFromRoot.reduce(
      (acc, r) => ({ ...acc, ...r.params }), {} as Record<string, string>
    );
    const unitId = allParams['unit-id'];
    this.loadData(unitId);
  }

  ngOnDestroy(): void {
    this.eventDialogRef?.close();
    this.destroy$.next();
    this.destroy$.complete();
  }

  private loadData(unitId: string): void {
    this.isLoading = true;
    this.errorMessage = null;

    this.unitService.getUnit(unitId).pipe(takeUntil(this.destroy$)).subscribe({
      next: unit => {
        if (unit.organizationLevelId !== OrganizationLevel.Comunidad) {
          this.router.navigate(['/units', unitId]);
          return;
        }
        this.unit = unit;
        this.breadcrumbService.setDynamicCrumb('unit-id', unit.name);
        this.loadEventTypes();
        this.loadEvents(unitId);
      },
      error: () => {
        this.isLoading = false;
        this.errorMessage = 'No se pudo cargar la comunidad.';
        this.cdr.markForCheck();
      },
    });
  }

  private loadEvents(unitId: string): void {
    this.unitService.getEvents(unitId).pipe(takeUntil(this.destroy$)).subscribe({
      next: events => {
        this.events = events;
        this.isLoading = false;
        this.cdr.markForCheck();
      },
      error: () => {
        this.isLoading = false;
        this.errorMessage = 'No se pudieron cargar los eventos.';
        this.cdr.markForCheck();
      },
    });
  }

  private loadEventTypes(): void {
    this.eventTypeService.getAll().pipe(takeUntil(this.destroy$)).subscribe({
      next: eventTypes => {
        this.eventTypeOptions = eventTypes;
        this.cdr.markForCheck();
      },
      error: () => {
        this.notifications.error('No se pudieron cargar los tipos de eventos.');
        this.cdr.markForCheck();
      },
    });
  }

  onShowAddForm(): void {
    if (!this.userFullInfoService.canManageMemberDataSnapshot() || !this.unit || this.eventDialogRef) {
      return;
    }

    this.editingEventId = null;
    this.eventForm.reset();
    this.openEventDialog();
  }

  onEditEvent(event: UnitEvent): void {
    if (!this.userFullInfoService.canManageMemberDataSnapshot() || !this.unit || this.eventDialogRef) {
      return;
    }

    this.editingEventId = event.id;
    this.eventForm.setValue({
      eventTypeId: event.eventTypeId,
      eventDate: this.dateAdapter.deserialize(event.eventDate),
      notes: event.notes ?? '',
    });
    this.openEventDialog();
  }

  onCancelEdit(): void {
    if (!this.isSaving) this.eventDialogRef?.close();
  }

  private openEventDialog(): void {
    this.eventDialogRef = this.dialog.open(this.eventDialog, modalDialogConfig({
      size: 'md', layout: 'form', disableClose: true,
    }));
    this.eventDialogRef.afterClosed().pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.eventDialogRef = undefined;
      this.editingEventId = null;
      this.eventForm.reset();
      this.cdr.markForCheck();
    });
  }

  onSubmit(): void {
    if (!this.userFullInfoService.canManageMemberDataSnapshot() || this.isSaving) {
      return;
    }

    if (this.eventForm.invalid || !this.unit) {
      this.eventForm.markAllAsTouched();
      return;
    }

    const { eventTypeId, notes } = this.eventForm.value;
    const date = this.eventForm.value.eventDate;
    const eventDate = `${this.dateAdapter.getYear(date)}-${String(this.dateAdapter.getMonth(date) + 1).padStart(2, '0')}-${String(this.dateAdapter.getDate(date)).padStart(2, '0')}`;
    const selectedEventType = this.eventTypeOptions.find(t => t.value === eventTypeId);
    const payload = { eventTypeId, eventDate, notes: notes || undefined };
    this.isSaving = true;

    if (this.editingEventId !== null) {
      const eventId = this.editingEventId;
      this.unitService.updateEvent(this.unit.id, eventId, payload)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: () => {
            this.events = this.events.map(e =>
              e.id === eventId
                ? { ...e, eventTypeId, eventDate, notes: notes || undefined, eventTypeName: selectedEventType?.name ?? e.eventTypeName }
                : e
            );
            this.isSaving = false;
            this.eventDialogRef?.close();
            this.notifications.success('Evento actualizado correctamente.');
            this.cdr.markForCheck();
          },
          error: (err) => {
            this.isSaving = false;
            const msg = err?.error?.message ?? err?.error?.title ?? 'No se pudo actualizar el evento.';
            this.notifications.error(msg);
            this.cdr.markForCheck();
          },
        });
    } else {
      this.unitService.createEvent(this.unit.id, payload)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: newId => {
            const newEvent: UnitEvent = {
              id: newId,
              eventTypeId,
              eventDate,
              notes: notes || undefined,
              eventTypeName: selectedEventType?.name ?? '',
            };
            this.events = [...this.events, newEvent].sort((a, b) => a.eventDate.localeCompare(b.eventDate));
            this.isSaving = false;
            this.eventDialogRef?.close();
            this.notifications.success('Evento registrado correctamente.');
            this.cdr.markForCheck();
          },
          error: (err) => {
            this.isSaving = false;
            const msg = err?.error?.message ?? err?.error?.title ?? 'No se pudo registrar el evento.';
            this.notifications.error(msg);
            this.cdr.markForCheck();
          },
        });
    }
  }

  onDelete(event: UnitEvent): void {
    if (!this.userFullInfoService.canManageMemberDataSnapshot()) {
      return;
    }

    if (!this.unit) return;

    const data: DeleteConfirmDialogData = {
      title: 'Eliminar evento',
      message: `¿Estás seguro de que querés eliminar el evento "${this.getEventTypeLabel(event)}" del ${event.eventDate}?`,
      confirmButtonText: 'Eliminar',
    };

    this.dialog
      .open(DeleteConfirmDialogComponent, modalDialogConfig({ data, size: 'sm', layout: 'confirm', tone: 'danger' }))
      .afterClosed()
      .pipe(filter(result => !!result), takeUntil(this.destroy$))
      .subscribe(() => {
        this.unitService.deleteEvent(this.unit!.id, event.id)
          .pipe(takeUntil(this.destroy$))
          .subscribe({
            next: () => {
              this.events = this.events.filter(e => e.id !== event.id);
              this.notifications.success('Evento eliminado.');
              this.cdr.markForCheck();
            },
            error: () => {
              this.notifications.error('No se pudo eliminar el evento.');
              this.cdr.markForCheck();
            },
          });
      });
  }
}
